import { Button, Buttons, CancelButton, Checkbox, Drawer, Field, Form, FormMessage, Radio, Table } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { Formik } from 'formik';
import { useActions, useDirtyCheck, useForm } from '~/hooks';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { capitalize, emptyStringToNull, mergeValues } from '~/utils';
import { colors } from '~/styles';
import * as Yup from 'yup';

const initialState = { isReady: false, activityTypes: null, opportunityTypes: null, pipelines: null };
const handlers = {
  ready: ({ activityTypes, opportunityTypes, pipelines }) => ({
    isReady: true,
    activityTypes,
    opportunityTypes,
    pipelines,
  }),
};

const StyledCheckbox = styled.div`
  > label > div {
    background: ${colors.white};
  }
`;

export default function SyncSettingsDrawer({ integration, onClose, onSave }) {
  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);
  const [{ status, message, isSubmitting }, form] = useForm();
  const [{ isReady, activityTypes, opportunityTypes, pipelines }, actions] = useActions(handlers, initialState);
  const api = useApi();
  const { workspace } = useWorkspace();

  useEffect(() => {
    (async () => {
      const [opportunityTypesResponse, activityTypesResponse, pipelinesResponse] = await Promise.all([
        api.www.workspaces(workspace.id).hubspot.getOpportunityTypes(),
        api.www.workspaces(workspace.id).hubspot.getActivityTypes(),
        api.www.workspaces(workspace.id).hubspot.getPipelines(),
      ]);

      const opportunityTypes = opportunityTypesResponse.data.map((type) => type.label);
      const activityTypes = activityTypesResponse.data;
      const pipelines = pipelinesResponse.data;

      actions.ready({ activityTypes, opportunityTypes, pipelines });
    })();
  }, [actions, api, workspace.id]);

  function handleClose() {
    if (onClose) {
      onClose();
    }
  }

  // Clean up invalid pipelines that have been deleted in Hubspot
  useEffect(() => {
    if (formRef.current) {
      const currentValues = formRef.current.values;
      const validIds = pipelines.map((p) => p.id);
      const currentSelected = currentValues.pipelines || [];
      const filtered = currentSelected.filter((id) => validIds.includes(id));
      formRef.current.setFieldValue('pipelines', filtered);
    }
  }, [pipelines]);

  const initialValues = mergeValues(
    {
      autoSync: false,
      syncStartDate: '',
      minimumProbability: '0',
      minimumAmount: '0',
      syncInterval: 240,
      opportunityTypes: [],
      pipelines: [],
      activityTypes: [],
      importedMemberAvailability: 'no_availability',
    },
    integration.settings,
  );
  if (!isReady) return null;
  return (
    <Drawer
      isOpen
      title="Sync Settings"
      byline="HubSpot Integration"
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={handleClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        async function handleSubmit(values) {
          try {
            form.submit();

            const body = emptyStringToNull({
              ...values,
            });

            await api.www.workspaces(workspace.id).hubspot.updateSyncSettings(body);

            form.save();
            onSave();
            closeDrawer();
          } catch ({ message }) {
            form.error({ message });
          }
        }

        return (
          <Formik
            innerRef={formRef}
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={Yup.object().shape({
              autoSync: Yup.bool().label('Automatically Sync').required('Automatically Sync must be selected'),

              syncInterval: Yup.number()
                .label('Sync Interval')
                .min(60, 'Sync Interval must be at least hourly')
                .max(1440, 'Sync Interval cannot exceed daily')
                .required('Sync Interval is required'),

              minimumProbability: Yup.number()
                .label('Minimum Probability')
                .min(0, 'Minimum Probability cannot be less than 0')
                .max(100, 'Minimum Probability cannot exceed 100')
                .nullable()
                .when('autoSync', {
                  is: true,
                  then: Yup.number().required('Minimum Probability is required when Auto Sync is enabled'),
                  otherwise: Yup.number().notRequired(),
                }),

              minimumAmount: Yup.number()
                .label('Minimum Amount')
                .min(0, 'Minimum Amount cannot be less than 0')
                .max(10000000000000, 'Minimum Amount is too large')
                .nullable(),

              syncStartDate: Yup.date().label('Start Date').nullable().required('Start Date is required'),

              opportunityTypes: Yup.array()
                .label('Opportunity Types')
                .of(Yup.string())
                .min(1, 'At least one Opportunity Type is required')
                .required('Opportunity Types are required'),

              pipelines: Yup.array()
                .label('Pipelines')
                .of(Yup.string())
                .min(1, 'At least one Pipeline is required')
                .required('Pipeline selection is required'),

              activityTypes: Yup.array().label('Activity Types').of(Yup.string()),

              importedMemberAvailability: Yup.string()
                .label('Imported Member Capacity')
                .required('Imported Member Capacity is required'),
            })}>
            {(formik) => {
              const { values, setFieldValue } = formik;
              const selectedPipelineIds = values.pipelines || [];
              const handlePipelineToggle = (pipelineId, isChecked) => {
                setFieldValue(
                  'pipelines',
                  isChecked
                    ? selectedPipelineIds.filter((id) => id !== pipelineId)
                    : [...selectedPipelineIds, pipelineId],
                );
              };

              return (
                <Form>
                  {status && <FormMessage.Error>{message}</FormMessage.Error>}
                  <Form.Section
                    title="Automatically Sync"
                    subtitle="If turned on, every hour changes in your HubSpot connection will sync changes to Ruddr.">
                    <Form.Control>
                      <Field.RadioGroup name="autoSync">
                        <Radio value={true} label="On" />
                        <Radio value={false} label="Off" />
                      </Field.RadioGroup>
                    </Form.Control>
                    <Form.Control>
                      <Field.SingleSelect
                        name="syncInterval"
                        placeholder="Frequency"
                        clearable={false}
                        disabled={!formik.values.autoSync}>
                        <option value={60}>Every Hour</option>
                        <option value={120}>Every Two Hours</option>
                        <option value={180}>Every Three Hours</option>
                        <option value={240}>Every Four Hours</option>
                        <option value={480}>Every Eight Hours</option>
                        <option value={720}>Every Twelve Hours</option>
                        <option value={1440}>Daily</option>
                      </Field.SingleSelect>
                    </Form.Control>
                  </Form.Section>
                  <Form.Section
                    title="Beginning"
                    subtitle="Any companies, opportunities, and contacts created prior to the Start Date will not be synced.">
                    <Form.Control>
                      <Field.DayPicker name="syncStartDate" placeholder="Start Date" clearable={false} />
                    </Form.Control>
                  </Form.Section>
                  <Form.Section
                    title="Probability"
                    subtitle="Any opportunities with less than these minimum probability amount values will not be synced.">
                    <Form.Control>
                      <Field.Number
                        name="minimumProbability"
                        placeholder="Minimum Probability"
                        min={0}
                        max={100}
                        precision={2}
                        prefix="%"
                        style={{ textAlign: 'left' }}
                      />
                    </Form.Control>
                    <Form.Control>
                      <Field.Number
                        name="minimumAmount"
                        placeholder="Minimum Amount"
                        min={0}
                        precision={2}
                        prefix="$"
                      />
                    </Form.Control>
                  </Form.Section>
                  {opportunityTypes && (
                    <Form.Section
                      title="Opportunity Types"
                      subtitle="Any opportunities with the selected types will be synced.">
                      <Form.Control>
                        <Field.CheckboxGroup direction="vertical" name="opportunityTypes">
                          <Checkbox
                            key={'null'}
                            value={'_NO TYPE'}
                            label={'Include Opportunities without a Type assigned'}
                          />
                          {opportunityTypes.map((option, index) => (
                            <Checkbox key={index} value={option} label={option} />
                          ))}
                        </Field.CheckboxGroup>
                      </Form.Control>
                    </Form.Section>
                  )}
                  {!opportunityTypes && <>Loading opportunity types from HubSpot...</>}
                  {pipelines && (
                    <Form.Section
                      title="Pipelines"
                      subtitle="Any opportunities with the selected pipelines will be synced.">
                      <Form.Control>
                        <Table small>
                          <Table.BoxHeader>
                            <Table.Column width="3rem">
                              <StyledCheckbox />
                            </Table.Column>
                            <Table.Column>Pipeline</Table.Column>
                          </Table.BoxHeader>
                          <Table.Body>
                            {pipelines.map((pipeline) => {
                              const isChecked = selectedPipelineIds.includes(pipeline.id);
                              return (
                                <Table.Row key={pipeline.id}>
                                  <Table.Cell>
                                    <StyledCheckbox>
                                      <Checkbox
                                        checked={isChecked}
                                        onChange={() => handlePipelineToggle(pipeline.id, isChecked)}
                                      />
                                    </StyledCheckbox>
                                  </Table.Cell>
                                  <Table.Cell>{pipeline.name}</Table.Cell>
                                </Table.Row>
                              );
                            })}
                          </Table.Body>
                        </Table>
                      </Form.Control>
                    </Form.Section>
                  )}
                  {!pipelines && <>Loading pipelines from HubSpot...</>}
                  {activityTypes && (
                    <Form.Section
                      title="Activity Types"
                      subtitle="Any activities with the selected types will be synced.">
                      <Form.Control>
                        <Field.CheckboxGroup direction="vertical" name="activityTypes">
                          {activityTypes.map((option, index) => (
                            <Checkbox key={index} value={option} label={capitalize(option)} />
                          ))}
                        </Field.CheckboxGroup>
                      </Form.Control>
                    </Form.Section>
                  )}
                  <Form.Section
                    title="Imported Member Capacity"
                    subtitle="When Ruddr imports a member, it can use the workspace default capacity for the member or no capacity.">
                    <Form.Control>
                      <Field.SingleSelect name="importedMemberAvailability" placeholder="Imported Member Capacity">
                        <option value="workspace_default">Workspace Default</option>
                        <option value="no_availability">No Capacity</option>
                      </Field.SingleSelect>
                    </Form.Control>
                  </Form.Section>
                  {!activityTypes && <>Loading activity types...</>}
                  <Drawer.Actions>
                    <Buttons align="right">
                      <CancelButton onClick={handleCloseClick}>Close</CancelButton>
                      <Button isLoading={isSubmitting} onClick={formik.submitForm}>
                        Save &amp; Close
                      </Button>
                    </Buttons>
                  </Drawer.Actions>
                </Form>
              );
            }}
          </Formik>
        );
      }}
    </Drawer>
  );
}
